import { useState, useEffect, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import {
  ShoppingCart,
  Check,
  HelpCircle,
  RotateCcw,
  ChevronDown,
  // Icons for options
  Circle,
  Square,
  Triangle,
  Star,
  Heart,
  Shield,
  Zap,
  Flame,
  Droplets,
  Wind,
  Sun,
  Moon,
  Cloud,
  Anchor,
  Ship,
  Compass,
  Navigation,
  Lock,
  Unlock,
  Eye,
  EyeOff,
  AlertTriangle,
  Info,
  Settings2,
  Wrench,
  Hammer,
  Gauge,
  Thermometer,
  Ruler,
  Box,
  Package,
  Layers,
  Grid3X3,
  LayoutGrid,
  type LucideIcon,
} from 'lucide-react'

// Icon map for dynamic rendering
const ICON_MAP: Record<string, LucideIcon> = {
  'circle': Circle,
  'square': Square,
  'triangle': Triangle,
  'star': Star,
  'heart': Heart,
  'shield': Shield,
  'zap': Zap,
  'flame': Flame,
  'droplets': Droplets,
  'wind': Wind,
  'sun': Sun,
  'moon': Moon,
  'cloud': Cloud,
  'anchor': Anchor,
  'ship': Ship,
  'compass': Compass,
  'navigation': Navigation,
  'lock': Lock,
  'unlock': Unlock,
  'eye': Eye,
  'eye-off': EyeOff,
  'check': Check,
  'alert-triangle': AlertTriangle,
  'info': Info,
  'help-circle': HelpCircle,
  'settings': Settings2,
  'wrench': Wrench,
  'hammer': Hammer,
  'gauge': Gauge,
  'thermometer': Thermometer,
  'ruler': Ruler,
  'box': Box,
  'package': Package,
  'layers': Layers,
  'grid': Grid3X3,
  'layout-grid': LayoutGrid,
}

const getIconComponent = (iconName: string | null): LucideIcon | null => {
  if (!iconName) return null
  return ICON_MAP[iconName] || null
}
import { cn } from '@/lib/utils'
import { useQuoteBasketStore } from '@/stores/quoteBasketStore'
import { supabase } from '@/lib/supabase'
import type { Product, ProductConfiguration, ConfiguratorTemplate } from '@/types'

interface ProductConfiguratorProps {
  product: Product
}

type DimensionUnit = 'mm' | 'inch'
type DimensionReference = 'clear_opening' | 'ships_opening'

export function ProductConfigurator({ product }: ProductConfiguratorProps) {
  const addItem = useQuoteBasketStore((state) => state.addItem)
  const [isAdded, setIsAdded] = useState(false)

  // Dimensions state
  const [unit, setUnit] = useState<DimensionUnit>('mm')
  const [width, setWidth] = useState('')
  const [height, setHeight] = useState('')
  const [reference, setReference] = useState<DimensionReference>('clear_opening')

  // Custom fields state
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, string | boolean>>({})

  // Fetch category to get template_id
  const { data: category } = useQuery({
    queryKey: ['category', product.category_id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('categories')
        .select('*, configurator_template_id')
        .eq('id', product.category_id)
        .single()
      if (error) throw error
      return data
    },
    enabled: !!product.category_id,
  })

  // Fetch template with fields
  const { data: template } = useQuery({
    queryKey: ['configurator-template', category?.configurator_template_id],
    queryFn: async () => {
      if (!category?.configurator_template_id) return null
      const { data, error } = await supabase
        .from('configurator_templates')
        .select(`
          *,
          fields:configurator_template_fields(
            *,
            options:configurator_field_options(*)
          )
        `)
        .eq('id', category.configurator_template_id)
        .single()
      if (error) throw error
      return data as ConfiguratorTemplate
    },
    enabled: !!category?.configurator_template_id,
  })

  // Initialize custom field defaults
  useEffect(() => {
    if (template?.fields) {
      const defaults: Record<string, string | boolean> = {}
      template.fields
        .sort((a, b) => a.sort_order - b.sort_order)
        .forEach((field) => {
          if (field.field_type === 'checkbox') {
            defaults[field.field_key] = false
          } else if (field.field_type === 'dropdown' || field.field_type === 'radio') {
            const defaultOption = field.options?.find((o) => o.is_default) || field.options?.[0]
            if (defaultOption) {
              defaults[field.field_key] = defaultOption.value
            }
          } else {
            defaults[field.field_key] = ''
          }
        })
      setCustomFieldValues(defaults)
    }
  }, [template])

  // Count active selections
  const activeSelectionsCount = useMemo(() => {
    let count = 0
    if (width || height) count++
    count += Object.values(customFieldValues).filter((v) => v !== '' && v !== false).length
    return count
  }, [width, height, customFieldValues])

  const handleReset = () => {
    setUnit('mm')
    setWidth('')
    setHeight('')
    setReference('clear_opening')
    // Reset custom fields to defaults
    if (template?.fields) {
      const defaults: Record<string, string | boolean> = {}
      template.fields.forEach((field) => {
        if (field.field_type === 'checkbox') {
          defaults[field.field_key] = false
        } else if (field.field_type === 'dropdown' || field.field_type === 'radio') {
          const defaultOption = field.options?.find((o) => o.is_default) || field.options?.[0]
          if (defaultOption) {
            defaults[field.field_key] = defaultOption.value
          }
        } else {
          defaults[field.field_key] = ''
        }
      })
      setCustomFieldValues(defaults)
    }
  }

  const handleAddToQuote = () => {
    const configuration: ProductConfiguration = {
      specifications: {},
    }

    // Add custom field values as specifications (convert booleans to strings)
    Object.entries(customFieldValues).forEach(([key, value]) => {
      if (value !== '' && value !== false) {
        configuration.specifications![key] = String(value)
      }
    })

    if (template?.show_dimensions && width && height) {
      configuration.dimensions = {
        unit,
        width: parseFloat(width),
        height: parseFloat(height),
        reference,
      }
    }

    addItem(product, configuration)
    setIsAdded(true)
    setTimeout(() => setIsAdded(false), 2000)
  }

  const updateCustomField = (key: string, value: string | boolean) => {
    setCustomFieldValues((prev) => ({ ...prev, [key]: value }))
  }

  // If no template, don't show configurator
  if (!template) {
    return null
  }

  const sortedFields = template.fields?.sort((a, b) => a.sort_order - b.sort_order) || []

  return (
    <div className="bg-white border border-secondary-200 rounded-xl">
      {/* Header */}
      <div className="px-5 py-4 border-b border-secondary-100">
        <h2 className="text-xl font-bold text-secondary-900">Product configurator</h2>
        <div className="flex items-center justify-between mt-1">
          <span className="text-sm text-secondary-500">
            {activeSelectionsCount} active selection{activeSelectionsCount !== 1 ? 's' : ''}
          </span>
          <button
            onClick={handleReset}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Reset selections
          </button>
        </div>
      </div>

      {/* Form */}
      <div className="p-5 space-y-6">
        {/* Unit of measure - Built-in */}
        {template.show_unit_selector && (
          <div>
            <label className="flex items-center gap-1.5 text-sm font-medium text-secondary-700 mb-3">
              Unit of measure
              <HelpCircle className="w-4 h-4 text-secondary-400" />
            </label>
            <div className="flex gap-2">
              <button
                onClick={() => setUnit('mm')}
                className={cn(
                  'px-5 py-2.5 text-sm font-medium rounded-lg border-2 transition-all',
                  unit === 'mm'
                    ? 'border-secondary-900 bg-white text-secondary-900'
                    : 'border-secondary-200 bg-white text-secondary-600 hover:border-secondary-300'
                )}
              >
                mm
              </button>
              <button
                onClick={() => setUnit('inch')}
                className={cn(
                  'px-5 py-2.5 text-sm font-medium rounded-lg border-2 transition-all',
                  unit === 'inch'
                    ? 'border-secondary-900 bg-white text-secondary-900'
                    : 'border-secondary-200 bg-white text-secondary-600 hover:border-secondary-300'
                )}
              >
                inches
              </button>
            </div>
          </div>
        )}

        {/* Width & Height - Built-in */}
        {template.show_dimensions && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="flex items-center gap-1.5 text-sm font-medium text-secondary-700 mb-2">
                Width
                <HelpCircle className="w-4 h-4 text-secondary-400" />
              </label>
              <input
                type="number"
                value={width}
                onChange={(e) => setWidth(e.target.value)}
                placeholder=""
                className="w-full px-4 py-2.5 border-2 border-secondary-200 rounded-lg text-secondary-900 placeholder:text-secondary-400 focus:outline-none focus:border-secondary-900 transition-colors"
              />
            </div>
            <div>
              <label className="flex items-center gap-1.5 text-sm font-medium text-secondary-700 mb-2">
                Height
                <HelpCircle className="w-4 h-4 text-secondary-400" />
              </label>
              <input
                type="number"
                value={height}
                onChange={(e) => setHeight(e.target.value)}
                placeholder=""
                className="w-full px-4 py-2.5 border-2 border-secondary-200 rounded-lg text-secondary-900 placeholder:text-secondary-400 focus:outline-none focus:border-secondary-900 transition-colors"
              />
            </div>
          </div>
        )}

        {/* Size Reference - Built-in */}
        {template.show_size_reference && (
          <div>
            <label className="flex items-center gap-1.5 text-sm font-medium text-secondary-700 mb-3">
              Size Reference
              <HelpCircle className="w-4 h-4 text-secondary-400" />
            </label>
            <div className="space-y-2">
              <button
                onClick={() => setReference('clear_opening')}
                className={cn(
                  'w-full px-4 py-3 text-sm font-medium rounded-lg border-2 text-left transition-all',
                  reference === 'clear_opening'
                    ? 'border-secondary-900 bg-white text-secondary-900'
                    : 'border-secondary-200 bg-white text-secondary-600 hover:border-secondary-300'
                )}
              >
                Clear opening
              </button>
              <button
                onClick={() => setReference('ships_opening')}
                className={cn(
                  'w-full px-4 py-3 text-sm font-medium rounded-lg border-2 text-left transition-all',
                  reference === 'ships_opening'
                    ? 'border-secondary-900 bg-white text-secondary-900'
                    : 'border-secondary-200 bg-white text-secondary-600 hover:border-secondary-300'
                )}
              >
                Bulkhead opening
              </button>
            </div>
          </div>
        )}

        {/* Custom Fields from Template */}
        {sortedFields.map((field) => (
          <div key={field.id}>
            <label className="flex items-center gap-1.5 text-sm font-medium text-secondary-700 mb-2">
              {field.label}
              {field.is_required && <span className="text-red-500">*</span>}
              {field.help_text && (
                <span title={field.help_text}>
                  <HelpCircle className="w-4 h-4 text-secondary-400" />
                </span>
              )}
            </label>

            {/* Dropdown */}
            {field.field_type === 'dropdown' && (() => {
              const selectedOption = field.options?.find(
                (o) => o.value === customFieldValues[field.field_key]
              )
              const SelectedIcon = selectedOption ? getIconComponent(selectedOption.icon) : null
              const hasSelectedIcon = selectedOption?.icon_url || SelectedIcon
              return (
                <div className="relative">
                  <select
                    value={String(customFieldValues[field.field_key] || '')}
                    onChange={(e) => updateCustomField(field.field_key, e.target.value)}
                    className={cn(
                      'w-full px-4 py-3 pr-10 border-2 border-secondary-200 rounded-lg text-secondary-900 font-medium appearance-none focus:outline-none focus:border-secondary-900 transition-colors cursor-pointer bg-white',
                      hasSelectedIcon && 'pl-14'
                    )}
                  >
                    {field.options
                      ?.sort((a, b) => a.sort_order - b.sort_order)
                      .map((option) => (
                        <option key={option.id} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                  </select>
                  {hasSelectedIcon && (
                    <span className="absolute left-0 top-0 bottom-0 w-12 flex items-center justify-center pointer-events-none">
                      {selectedOption?.icon_url ? (
                        <img src={selectedOption.icon_url} alt="" className="w-full h-full object-contain p-1" />
                      ) : SelectedIcon ? (
                        <SelectedIcon className="w-7 h-7 text-secondary-600" />
                      ) : null}
                    </span>
                  )}
                  <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary-400 pointer-events-none" />
                </div>
              )
            })()}

            {/* Radio Buttons */}
            {field.field_type === 'radio' && (
              <div className="space-y-2">
                {field.options
                  ?.sort((a, b) => a.sort_order - b.sort_order)
                  .map((option) => {
                    const IconComponent = getIconComponent(option.icon)
                    const hasIcon = option.icon_url || IconComponent
                    return (
                      <button
                        key={option.id}
                        onClick={() => updateCustomField(field.field_key, option.value)}
                        className={cn(
                          'w-full text-sm font-medium rounded-lg border-2 text-left transition-all flex items-center justify-between overflow-hidden',
                          hasIcon ? 'pl-4 pr-0 py-0' : 'px-4 py-3',
                          customFieldValues[field.field_key] === option.value
                            ? 'border-secondary-900 bg-white text-secondary-900'
                            : 'border-secondary-200 bg-white text-secondary-600 hover:border-secondary-300'
                        )}
                      >
                        <span className={cn(hasIcon && 'py-3')}>{option.label}</span>
                        {hasIcon && (
                          <span className="flex-shrink-0 h-[46px] w-[46px] flex items-center justify-center">
                            {option.icon_url ? (
                              <img src={option.icon_url} alt="" className="h-full w-full object-contain" />
                            ) : IconComponent ? (
                              <IconComponent className="w-7 h-7" />
                            ) : null}
                          </span>
                        )}
                      </button>
                    )
                  })}
              </div>
            )}

            {/* Text Input */}
            {field.field_type === 'text' && (
              <input
                type="text"
                value={String(customFieldValues[field.field_key] || '')}
                onChange={(e) => updateCustomField(field.field_key, e.target.value)}
                placeholder={field.placeholder || ''}
                className="w-full px-4 py-2.5 border-2 border-secondary-200 rounded-lg text-secondary-900 placeholder:text-secondary-400 focus:outline-none focus:border-secondary-900 transition-colors"
              />
            )}

            {/* Number Input */}
            {field.field_type === 'number' && (
              <input
                type="number"
                value={String(customFieldValues[field.field_key] || '')}
                onChange={(e) => updateCustomField(field.field_key, e.target.value)}
                placeholder={field.placeholder || ''}
                className="w-full px-4 py-2.5 border-2 border-secondary-200 rounded-lg text-secondary-900 placeholder:text-secondary-400 focus:outline-none focus:border-secondary-900 transition-colors"
              />
            )}

            {/* Checkbox */}
            {field.field_type === 'checkbox' && (
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={customFieldValues[field.field_key] === true}
                  onChange={(e) => updateCustomField(field.field_key, e.target.checked)}
                  className="w-5 h-5 rounded border-secondary-300"
                />
                <span className="text-sm text-secondary-700">{field.placeholder || 'Yes'}</span>
              </label>
            )}
          </div>
        ))}
      </div>

      {/* Add to Quote Button */}
      <div className="px-5 pb-5">
        <motion.button
          onClick={handleAddToQuote}
          disabled={isAdded}
          className={cn(
            'w-full py-3.5 rounded-lg font-semibold text-base flex items-center justify-center gap-2 transition-all',
            isAdded
              ? 'bg-green-600 text-white'
              : 'bg-primary-600 text-white hover:bg-primary-700'
          )}
          whileTap={{ scale: 0.98 }}
        >
          {isAdded ? (
            <>
              <Check className="w-5 h-5" />
              Added to Quote
            </>
          ) : (
            <>
              <ShoppingCart className="w-5 h-5" />
              Add to Quote Basket
            </>
          )}
        </motion.button>
      </div>
    </div>
  )
}
